import { styled } from "@linaria/react";
import type { HTMLAttributes } from "react";
import React from "react";
import type { DropzoneOptions, DropzoneProps } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { color, fonts } from "..";
import t from "../i18n";
import Button from "../Button";
import {
  CheckboxCircleLine,
  QuestionLine,
  DownloadLine,
  ErrorWarningLine,
  InformationLine,
  UploadLine,
} from "../IconV2/react";
import Spinner from "../Spinner";
import Text from "../Text";
import Link from "../Link";
import Badge from "../Badge";
import Details from "../Details";
import ButtonIconV2 from "../ButtonIconV2";
import TooltipV2 from "../TooltipV2";
import OverlayWrapper from "../TooltipV2/OverlayWrapper";
import { css } from "@linaria/core";

const DropZoneV2 = styled.div`
  & > *:not(:first-child) {
    margin-top: 8px;
  }
`;

type WrapperProps = {
  isDragActive: boolean;
} & HTMLAttributes<HTMLDivElement>;
const Wrapper = styled.div<WrapperProps>`
  cursor: pointer;
  padding: 24px 32px;

  display: flex;
  flex-direction: column;
  align-items: center;

  border-radius: 4px;
  background: ${color.titanium50};
  outline: none;

  border: ${props =>
    props.isDragActive
      ? `2px dashed ${color.raw.lightNitrogen500_Main}`
      : `2px dashed ${color.titanium300}`};

  opacity: ${props => (props.isDragActive ? 0.5 : 1)};

  &:hover {
    border: 2px dashed ${color.raw.lightNitrogen500_Main};
  }
`;

const LinkButton = styled(Button)`
  margin-top: 24px;
  height: auto;
  font: ${fonts.body2};
`;
const StyledLink = styled(Link)`
  display: block;
  white-space: nowrap;
  color: ${color.lightNitrogen500_Main};
  &:is(:hover, :active) {
    text-decoration: underline;
  }
  &:hover {
    color: ${color.lightNitrogen600};
  }
  &:active {
    color: ${color.lightNitrogen700};
  }
`;

const TextWordBreak = styled(Text)`
  font: ${fonts.body1SemiBold};
  word-break: break-all;
`;
const TextFileName = styled(Text)`
  margin-top: 16px;
  text-align: center;
`;
const TextDefaultImport = styled(Text)`
  margin-top: 16px;
  margin-bottom: 8px;
`;
const TextCapitalize = styled(Text)`
  text-transform: capitalize;
`;
const textRegularBody2 = css`
  font: ${fonts.body2};
`;

const ExpectedFormatContainer = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 8px;
`;
const InvalidMessageContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
  color: ${color.oxygen400};

  button {
    top: -5px;
    margin-right: 5px;
    overflow: visible;
  }
`;
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0px;
  gap: 16px;
`;

const ONE_MO = 1_048_576;

export interface P {
  onDrop: DropzoneProps["onDrop"];
  accept?: string | string[];
  maxSize?: DropzoneProps["maxSize"];
  multiple?: DropzoneProps["multiple"];
  isLoading?: DropzoneProps["disabled"];
  hasMissingInfo?: boolean;
  pathSampleFile?: string | { [key: string]: string };
  validation?: boolean;
  nameFile?: string | string[];
  errorText?: React.ReactNode;
  placeholderErrorText?: string;
  downloadErrors?: () => void;
  version?: string;
  className?: string;
}

const Loader = () => (
  <LoadingContainer>
    <Spinner height={40} width={40} />
    <Text type="body1">{t("dropzoneV2.loading")}</Text>
  </LoadingContainer>
);

const ExpectedFormat = ({
  accept,
  pathSampleFile,
  isMultipleFileMode,
}: {
  accept?: P["accept"];
  pathSampleFile?: P["pathSampleFile"];
  isMultipleFileMode: boolean;
}) => (
  <ExpectedFormatContainer>
    {accept ? (
      <Text type="body2" style={{ color: color.carbon800 }}>
        {t("dropzoneV2.expected_file_format")}{" "}
        {typeof pathSampleFile === "string" ? (
          <Text type="body2SemiBold" as="span">
            {Array.isArray(accept) && !isMultipleFileMode
              ? accept.join(` ${t("dropzoneV2.accepted_files.separator.or")} `)
              : accept}
          </Text>
        ) : Array.isArray(accept) ? (
          accept.map((a, i, arr) =>
            pathSampleFile?.[a] ? (
              <>
                <StyledLink
                  key={JSON.stringify({ a, i })}
                  download
                  href={pathSampleFile[a]}
                  onClick={e => e.stopPropagation()}
                  style={{ display: "inline", fontWeight: "600" }}
                >
                  {a}
                </StyledLink>
                {arr.length - 1 === i ? ". " : ", "}
              </>
            ) : (
              <>
                {a}
                {arr.length - 1 === i ? ". " : ", "}
              </>
            ),
          )
        ) : null}
      </Text>
    ) : null}
    {typeof pathSampleFile === "string" ? (
      <StyledLink download href={pathSampleFile} onClick={e => e.stopPropagation()}>
        <Text type="body2" style={{ display: "block" }}>
          {t("dropzoneV2.sample_File")}
        </Text>
      </StyledLink>
    ) : null}
  </ExpectedFormatContainer>
);

const InvalidMessage = ({
  placeholderErrorText,
  errorTextAcceptedFiles,
  errorText,
  downloadErrors,
}: {
  placeholderErrorText: P["placeholderErrorText"];
  errorTextAcceptedFiles: string;
  errorText: P["errorText"];
  downloadErrors: P["downloadErrors"];
}) => (
  <InvalidMessageContainer>
    <Details
      openDelay={1500}
      className={textRegularBody2}
      text={errorText || errorTextAcceptedFiles || placeholderErrorText}
    />
    {downloadErrors && !errorTextAcceptedFiles ? (
      <TooltipV2
        placement="right"
        overlay={<OverlayWrapper text={t("dropzoneV2.tooltip.download_error_message")} />}
        openDelay={1500}
      >
        <ButtonIconV2 onClick={downloadErrors}>
          <DownloadLine color={color.carbon800} size="s" />
        </ButtonIconV2>
      </TooltipV2>
    ) : null}
  </InvalidMessageContainer>
);

const DropzoneV2 = ({
  onDrop,
  accept,
  maxSize,
  pathSampleFile,
  isLoading,
  validation,
  nameFile,
  errorText,
  hasMissingInfo,
  placeholderErrorText = t("dropzoneV2.message.no_error_message_defined"),
  downloadErrors,
  version,
  className,
  multiple,
}: P) => {
  const [loading, setLoading] = React.useState(false);
  if (!onDrop) throw new Error("missing onDrop");
  const handleDrop: typeof onDrop = React.useCallback(
    (...args) => {
      try {
        setLoading(true);
        onDrop(...args);
      } finally {
        setLoading(false);
      }
    },
    [onDrop],
  );

  const options: DropzoneOptions = {
    onDrop: handleDrop,
    disabled: isLoading ?? loading,
    accept,
    maxSize,
    /**
     * react-dropzone consider multiple as true by default
     */
    multiple: Boolean(multiple),
  };
  const dropzone = useDropzone(options);

  const errorTextRejectedFiles = React.useMemo(
    () =>
      accept
        ? dropzone.rejectedFiles
            ?.filter(file => file.type !== accept)
            .map(file => {
              if (maxSize !== undefined && file.size > maxSize) {
                const maxSizeInMo = maxSize / ONE_MO;
                return t("dropzoneV2.max_size_file.text", maxSizeInMo);
              }
              return t("dropzoneV2.rejectedFiles.message", {
                name: file.name,
                accept: accept as string, //NOTE: If you don't have accept, then you can't have accept.
              });
            })
            .join(" ")
        : "",
    [dropzone.rejectedFiles, accept, maxSize],
  );

  const isValidating = validation === undefined && !errorTextRejectedFiles;
  const isValid = validation === true;

  return (
    <DropZoneV2>
      <Wrapper
        {...dropzone.getRootProps()}
        data-cy="dropzoneV2"
        className={className}
        isDragActive={dropzone.isDragActive}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <input {...dropzone.getInputProps()} />
            {isValidating ? (
              <>
                <UploadLine height={40} width={40} color={color.carbon800} />
                <TextDefaultImport type="body1">
                  {t("dropzone.drop_here", {
                    oneFile: !multiple,
                  })}
                </TextDefaultImport>
                <Button type="button" variant="secondary">
                  {t("dropzoneV2.select_files", {
                    oneFile: !multiple,
                  })}
                </Button>
                <ExpectedFormat
                  accept={accept}
                  pathSampleFile={pathSampleFile}
                  isMultipleFileMode={
                    multiple ?? true /* react-dropzone consider multiple as true by default  */
                  }
                />
                {maxSize ? (
                  <Text type="body2" style={{ display: "block", color: color.carbon800 }}>
                    {t("dropzoneV2.max_size", maxSize / ONE_MO)}
                  </Text>
                ) : null}
              </>
            ) : (
              <>
                {isValid ? (
                  <>
                    {hasMissingInfo ? (
                      <QuestionLine height={40} width={40} color={color.darkNitrogen600} />
                    ) : (
                      <CheckboxCircleLine height={40} width={40} color={color.chlorine600} />
                    )}
                    <TextFileName type="body1">
                      {multiple && Array.isArray(nameFile) ? (
                        <>
                          {hasMissingInfo
                            ? t("dropzoneV2.valid_files.missing_info.text", nameFile.length)
                            : t("dropzoneV2.valid_files.text", nameFile.length)}
                        </>
                      ) : (
                        <>
                          {hasMissingInfo
                            ? t("dropzoneV2.valid_file.missing_info.text", {
                                nameFile: (
                                  <TextWordBreak as="span">
                                    {nameFile || dropzone.rejectedFiles[0]?.name}
                                  </TextWordBreak>
                                ),
                              })
                            : t("dropzoneV2.valid_file.text", {
                                nameFile: (
                                  <TextWordBreak as="span">
                                    {nameFile || dropzone.rejectedFiles[0]?.name}
                                  </TextWordBreak>
                                ),
                              })}
                        </>
                      )}
                    </TextFileName>
                  </>
                ) : (
                  <>
                    <ErrorWarningLine height={40} width={40} color={color.oxygen400} />
                    <TextFileName type="body1">
                      {multiple && Array.isArray(nameFile) ? (
                        <>{t("dropzoneV2.no_valid_files.text", nameFile.length)}</>
                      ) : (
                        <>
                          {t("dropzoneV2.no_valid_file.text", {
                            nameFile: (
                              <TextWordBreak as="span">
                                {nameFile || dropzone.rejectedFiles[0]?.name}
                              </TextWordBreak>
                            ),
                          })}
                        </>
                      )}
                    </TextFileName>
                  </>
                )}
                {version ? (
                  <Badge
                    size="s"
                    icon={<InformationLine color={color.darkNitrogen600} />}
                    color={color.carbon900}
                    backgroundColor={color.lightNitrogen50}
                    style={{ marginTop: "8px" }}
                  >
                    <Text type="body3" as="span">
                      {t("dropzoneV2.file_version_detected")}
                    </Text>{" "}
                    <TextCapitalize type="body3" as="span">
                      {version}
                    </TextCapitalize>
                  </Badge>
                ) : null}
                <LinkButton leftIcon={<UploadLine />} type="button" variant="link">
                  {t("dropzoneV2.valid.select_files", {
                    oneFile: !multiple,
                  })}
                </LinkButton>
              </>
            )}
          </>
        )}
      </Wrapper>
      {(validation === false || (validation === undefined && errorTextRejectedFiles)) &&
      (errorText || downloadErrors) ? (
        <InvalidMessage
          errorTextAcceptedFiles={errorTextRejectedFiles}
          errorText={errorText}
          downloadErrors={downloadErrors}
          placeholderErrorText={placeholderErrorText}
        />
      ) : null}
    </DropZoneV2>
  );
};

export default React.memo(DropzoneV2);
